import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { motion } from 'framer-motion';

import THEME from '../../constants/theme';
import { useIntersectionObserver } from '../../hooks';

/**
 * FlowDiagram component for displaying a flow/process with boxes and arrows
 *
 * @param {Object} props - Component props
 * @param {Array} props.steps - Array of step labels
 * @param {string} props.backgroundColor - Background color for boxes (default: transparent)
 * @param {string} props.textColor - Text color (default: NEUTRAL_BLACK)
 * @param {string} props.arrowColor - Arrow icon color (default: PRIMARY_BLUE)
 * @param {Object} props.sx - Additional MUI styling
 * @returns {React.Component} FlowDiagram component
 */
const FlowDiagram = ({
  steps = [],
  backgroundColor = 'transparent',
  textColor = THEME.COLORS.NEUTRAL_BLACK,
  arrowColor = THEME.COLORS.PRIMARY_BLUE,
  sx = {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const flowRef = React.useRef(null);
  const isVisible = useIntersectionObserver(flowRef);

  return (
    <Box
      ref={flowRef}
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{
        opacity: isVisible ? 1 : 0,
        y: isVisible ? 0 : 20
      }}
      transition={{ duration: 0.6 }}
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? THEME.SPACING.LARGE : THEME.SPACING.XL,
        padding: THEME.SPACING.XL,
        backgroundColor: backgroundColor,
        ...sx
      }}
    >
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          {/* Step Box */}
          <Box
            component={motion.div}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{
              scale: isVisible ? 1 : 0.8,
              opacity: isVisible ? 1 : 0
            }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: `${THEME.SPACING.LARGE} ${THEME.SPACING.XL}`,
              border:
                backgroundColor === THEME.COLORS.PRIMARY_BLUE
                  ? 'none'
                  : `2px solid ${arrowColor}`,
              minWidth: isMobile ? '100%' : '150px',
              textAlign: 'center',
              backgroundColor: 'transparent',
              transition: `transform ${THEME.TRANSITIONS.MEDIUM}, box-shadow ${THEME.TRANSITIONS.MEDIUM}`,
              '&:hover': {
                transform:
                  backgroundColor === THEME.COLORS.PRIMARY_BLUE
                    ? 'none'
                    : 'translateY(-3px)',
                boxShadow:
                  backgroundColor === THEME.COLORS.PRIMARY_BLUE
                    ? 'none'
                    : THEME.SHADOWS.MEDIUM
              }
            }}
          >
            <Typography
              variant='h6'
              sx={{
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                color:
                  backgroundColor === THEME.COLORS.PRIMARY_BLUE
                    ? THEME.COLORS.NEUTRAL_WHITE
                    : textColor,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
              }}
            >
              {step}
            </Typography>
          </Box>

          {/* Arrow Icon (not after last step) */}
          {index < steps.length - 1 && (
            <Box
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: isVisible ? 1 : 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: arrowColor
              }}
            >
              {isMobile ? (
                <ArrowDownwardIcon sx={{ fontSize: '2rem' }} />
              ) : (
                <ArrowForwardIcon sx={{ fontSize: '2rem' }} />
              )}
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default FlowDiagram;
